/**
 * BudgetPDFPreview Component
 *
 * Visualização de PDF e sistema de assinatura digital para orçamentos
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  FileText,
  Download,
  Eye,
  EyeOff,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Printer,
  Share2,
  Edit,
  Save,
  X,
  Upload,
  CheckCircle,
  AlertCircle,
  Clock,
  User,
  Calendar,
  Shield,
  Lock,
  Unlock,
  PenTool,
  Signature,
  Type,
  Square,
  Circle,
  Image,
  Palette,
  Move,
  Trash2,
  Plus,
  Settings,
  Info,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2
} from 'lucide-react';

interface SignatureField {
  id: string;
  type: 'signature' | 'text' | 'date' | 'initials';
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  signed: boolean;
  signedBy?: {
    name: string;
    email: string;
    timestamp: string;
    signature: string;
  };
  label?: string;
  placeholder?: string;
}

interface PDFPreviewProps {
  budget: {
    id: string;
    number: string;
    client: {
      name: string;
      email: string;
    };
    total: number;
    status: string;
    items: any[];
  };
  pdfUrl?: string;
  onGeneratePDF?: (options: GeneratePDFOptions) => void;
  onSignDocument?: (signatures: Record<string, any>) => void;
  onDownloadPDF?: () => void;
  onPrintPDF?: () => void;
  onSharePDF?: () => void;
  allowSignature?: boolean;
  allowEditing?: boolean;
  allowDownload?: boolean;
  allowPrint?: boolean;
  allowShare?: boolean;
  requireSignature?: boolean;
  signatureFields?: SignatureField[];
  className?: string;
}

interface GeneratePDFOptions {
  includeWatermark?: boolean;
  watermarkText?: string;
  includeSignatureFields?: boolean;
  password?: string;
  quality: 'low' | 'medium' | 'high';
  format: 'A4' | 'Letter';
}

export function BudgetPDFPreview({
  budget,
  pdfUrl,
  onGeneratePDF,
  onSignDocument,
  onDownloadPDF,
  onPrintPDF,
  onSharePDF,
  allowSignature = true,
  allowEditing = false,
  allowDownload = true,
  allowPrint = true,
  allowShare = true,
  requireSignature = false,
  signatureFields = [],
  className
}: PDFPreviewProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Signature states
  const [signatureMode, setSignatureMode] = useState<'draw' | 'text' | 'upload'>('draw');
  const [signatureData, setSignatureData] = useState<Record<string, any>>({});
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentSignatureField, setCurrentSignatureField] = useState<string | null>(null);
  const [signaturePad, setSignaturePad] = useState<string>('');

  // PDF generation options
  const [pdfOptions, setPdfOptions] = useState<GeneratePDFOptions>({
    quality: 'high',
    format: 'A4',
    includeSignatureFields: allowSignature
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // Initialize signature pad
  useEffect(() => {
    if (canvasRef.current && signatureMode === 'draw' && currentSignatureField) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000000';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [signatureMode, currentSignatureField]);

  const handleGeneratePDF = () => {
    setIsLoading(true);
    setLoadingProgress(0);

    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    onGeneratePDF?.(pdfOptions);

    setTimeout(() => {
      setLoadingProgress(100);
      setIsLoading(false);
      clearInterval(progressInterval);
    }, 2000);
  };

  const handleDownloadPDF = () => {
    onDownloadPDF?.();
  };

  const handlePrintPDF = () => {
    onPrintPDF?.();
  };

  const handleSharePDF = () => {
    onSharePDF?.();
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleSignatureDraw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handleSignatureMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;

    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handleSignatureMouseUp = () => {
    if (!isDrawing || !canvasRef.current) return;

    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const signatureDataUrl = canvas.toDataURL();
      setSignatureData(prev => ({
        ...prev,
        [currentSignatureField!]: {
          type: 'draw',
          data: signatureDataUrl,
          timestamp: new Date().toISOString()
        }
      }));
    }
  };

  const handleTextSignature = (text: string) => {
    setSignatureData(prev => ({
      ...prev,
      [currentSignatureField!]: {
        type: 'text',
        data: text,
        timestamp: new Date().toISOString()
      }
    }));
  };

  const handleUploadSignature = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setSignatureData(prev => ({
        ...prev,
        [currentSignatureField!]: {
          type: 'upload',
          data: dataUrl,
          timestamp: new Date().toISOString()
        }
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleClearSignature = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    setSignatureData(prev => {
      const newData = { ...prev };
      delete newData[currentSignatureField!];
      return newData;
    });
  };

  const handleSignDocument = () => {
    const signedFields = Object.keys(signatureData);
    if (requireSignature && signedFields.length === 0) {
      return;
    }

    const signatures: Record<string, any> = {};
    signedFields.forEach(fieldId => {
      const field = signatureFields.find(f => f.id === fieldId);
      if (field) {
        signatures[fieldId] = {
          ...signatureData[fieldId],
          signedBy: {
            name: 'Usuário Atual',
            email: 'usuario@empresa.com'
          },
          fieldInfo: field
        };
      }
    });

    onSignDocument?.(signatures);
    setSignDialogOpen(false);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <div className={className}>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Eye className="h-4 w-4 mr-2" />
            Visualizar PDF
          </Button>
        </DialogTrigger>
        <DialogContent className={`max-w-6xl ${isFullscreen ? 'w-screen h-screen max-w-none' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Visualização de PDF - Orçamento {budget.number}
            </DialogTitle>
            <DialogDescription>
              Visualização em tempo real do documento PDF
            </DialogDescription>
          </DialogHeader>

          {/* PDF Controls */}
          <div className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoom <= 50}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium w-12 text-center">{zoom}%</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoom >= 200}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRotate}
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFullscreen}
              >
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">
                Página {currentPage} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              {allowDownload && (
                <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              )}
              {allowPrint && (
                <Button variant="outline" size="sm" onClick={handlePrintPDF}>
                  <Printer className="h-4 w-4 mr-2" />
                  Imprimir
                </Button>
              )}
              {allowShare && (
                <Button variant="outline" size="sm" onClick={handleSharePDF}>
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartilhar
                </Button>
              )}
              {allowSignature && (
                <Button size="sm" onClick={() => setSignDialogOpen(true)}>
                  <PenTool className="h-4 w-4 mr-2" />
                  Assinar Documento
                </Button>
              )}
            </div>
          </div>

          {/* PDF Content */}
          <div className="flex-1 bg-muted/20 rounded-lg overflow-hidden" ref={pdfContainerRef}>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-96 space-y-4">
                <FileText className="h-12 w-12 text-muted-foreground" />
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Gerando PDF...</span>
                    <span>{loadingProgress}%</span>
                  </div>
                  <Progress value={loadingProgress} />
                </div>
              </div>
            ) : pdfUrl ? (
              <div className="h-96 overflow-auto">
                <img
                  src={pdfUrl}
                  alt={`Orçamento ${budget.number} - Página ${currentPage}`}
                  className="mx-auto shadow-lg"
                  style={{
                    transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                    transformOrigin: 'center',
                    transition: 'transform 0.3s ease'
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-96 space-y-4">
                <FileText className="h-12 w-12 text-muted-foreground" />
                <div className="text-center space-y-2">
                  <h3 className="font-medium">PDF não gerado</h3>
                  <p className="text-sm text-muted-foreground">
                    Clique no botão abaixo para gerar o PDF do orçamento
                  </p>
                  <Button onClick={handleGeneratePDF}>
                    <FileText className="h-4 w-4 mr-2" />
                    Gerar PDF
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* PDF Options */}
          {!pdfUrl && (
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              <h4 className="font-medium">Opções de Geração</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Qualidade</Label>
                  <Select
                    value={pdfOptions.quality}
                    onValueChange={(value: 'low' | 'medium' | 'high') => setPdfOptions({ ...pdfOptions, quality: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baixa (Rápido)</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="high">Alta (Qualidade máxima)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Formato</Label>
                  <Select
                    value={pdfOptions.format}
                    onValueChange={(value: 'A4' | 'Letter') => setPdfOptions({ ...pdfOptions, format: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4</SelectItem>
                      <SelectItem value="Letter">Carta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="include-signature-fields"
                  checked={pdfOptions.includeSignatureFields}
                  onCheckedChange={(checked) => setPdfOptions({ ...pdfOptions, includeSignatureFields: checked })}
                />
                <Label htmlFor="include-signature-fields">Incluir campos de assinatura</Label>
              </div>
              {allowEditing && (
                <div className="flex items-center space-x-2">
                  <Switch
                    id="include-watermark"
                    checked={pdfOptions.includeWatermark}
                    onCheckedChange={(checked) => setPdfOptions({ ...pdfOptions, includeWatermark: checked })}
                  />
                  <Label htmlFor="include-watermark">Incluir marca d'água</Label>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5" />
              Assinar Documento Digitalmente
            </DialogTitle>
            <DialogDescription>
              Assine o orçamento {budget.number} digitalmente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Signature Fields */}
            {signatureFields.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium">Campos de Assinatura</h4>
                <div className="grid gap-3">
                  {signatureFields.map((field) => {
                    const isSigned = !!signatureData[field.id];
                    return (
                      <div
                        key={field.id}
                        className={`p-4 border rounded-lg ${
                          field.required && !isSigned ? 'border-red-200 bg-red-50' : 'border-border'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{field.label || 'Assinatura'}</span>
                            {field.required && <Badge variant="destructive" className="text-xs">Obrigatório</Badge>}
                            {isSigned && (
                              <Badge variant="default" className="text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Assinado
                              </Badge>
                            )}
                          </div>
                          {!isSigned && (
                            <Button
                              size="sm"
                              onClick={() => setCurrentSignatureField(field.id)}
                            >
                              <PenTool className="h-4 w-4 mr-1" />
                              Assinar
                            </Button>
                          )}
                        </div>
                        {isSigned && (
                          <div className="text-sm text-muted-foreground">
                            Assinado por: {signatureData[field.id].signedBy?.name} •{' '}
                            {new Date(signatureData[field.id].timestamp).toLocaleString('pt-BR')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Current Signature Field */}
            {currentSignatureField && (
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">
                    Assinando: {signatureFields.find(f => f.id === currentSignatureField)?.label || 'Campo de Assinatura'}
                  </h4>
                  <Button variant="ghost" size="sm" onClick={() => setCurrentSignatureField(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <Tabs value={signatureMode} onValueChange={(value: any) => setSignatureMode(value)}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="draw" className="flex items-center gap-2">
                      <PenTool className="h-4 w-4" />
                      Desenhar
                    </TabsTrigger>
                    <TabsTrigger value="text" className="flex items-center gap-2">
                      <Type className="h-4 w-4" />
                      Digitar
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Enviar
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="draw" className="space-y-4">
                    <div className="border-2 border-dashed border-border rounded-lg p-4">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={200}
                        className="border border-border rounded cursor-crosshair bg-card"
                        onMouseDown={handleSignatureMouseDown}
                        onMouseMove={handleSignatureDraw}
                        onMouseUp={handleSignatureMouseUp}
                        onMouseLeave={handleSignatureMouseUp}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleClearSignature}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Limpar
                      </Button>
                      <Button onClick={() => handleSignatureMouseUp()}>
                        <Save className="h-4 w-4 mr-2" />
                        Usar Assinatura
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="text" className="space-y-4">
                    <div>
                      <Label>Digite sua assinatura</Label>
                      <Input
                        placeholder="Digite seu nome completo"
                        className="text-lg font-signature"
                        onChange={(e) => handleTextSignature(e.target.value)}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="upload" className="space-y-4">
                    <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mb-2">
                        Envie uma imagem da sua assinatura
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files?.[0] && handleUploadSignature(e.target.files[0])}
                        className="hidden"
                        id="signature-upload"
                      />
                      <label htmlFor="signature-upload">
                        <Button variant="outline" asChild>
                          <span>Selecionar Arquivo</span>
                        </Button>
                      </label>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}

            {/* Security Information */}
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertTitle>Segurança da Assinatura Digital</AlertTitle>
              <AlertDescription>
                Esta assinatura digital é protegida por criptografia e possui validade legal conforme a legislação brasileira.
                A assinatura será registrada com carimbo de data/hora e vinculada permanentemente a este documento.
              </AlertDescription>
            </Alert>

            {/* Document Summary */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium mb-3">Resumo do Documento</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Número:</span>
                  <p className="font-medium">{budget.number}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Cliente:</span>
                  <p className="font-medium">{budget.client.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Valor Total:</span>
                  <p className="font-medium">{formatCurrency(budget.total)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status:</span>
                  <p className="font-medium">{budget.status}</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSignDocument}
              disabled={requireSignature && Object.keys(signatureData).length === 0}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Assinar Documento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}